import { useNavigate } from 'react-router-dom';
import { EmployeeType } from '@prisma/client';
import { Button, Form, Input, message, Space } from 'antd';
import { useWatch } from 'antd/es/form/Form';
import { ColumnsType } from 'antd/es/table';
import _ from 'lodash';

import { WorkerDto } from '@app/integ-apigee-interfaces';
import { SelectTable } from '@app/web-components';

import { employeeSubtypeOptions } from '../../access/pages';
import {
  AccessRuleExceptionWithDetail,
  useUpdateUserAccessRuleExceptions,
} from '../../rules/data/access-rule-exceptions';
import { AccessRuleWithDetail } from '../pages/manage';

type FormData = {
  justification: string;
  accessConflicts: number[];
  departmentConflicts: number[];
};

type ManageExceptionsFormProps = {
  targetUserExceptions: AccessRuleExceptionWithDetail[];
  loggedInUserRules: AccessRuleWithDetail[];
  user: WorkerDto;
  onReset: () => void;
  onSuccess: () => void;
};

export const ManageExceptionsForm = ({
  targetUserExceptions,
  loggedInUserRules,
  user,
  onReset,
  onSuccess,
}: ManageExceptionsFormProps) => {
  const [form] = Form.useForm<FormData>();
  const manage = useUpdateUserAccessRuleExceptions(user.enterpriseId);
  const navigate = useNavigate();
  const [messageApi, contextHolder] = message.useMessage();

  // Watches if new conflicts were added. Justification will not be required if changes are deletes-only
  const accessConflicts = useWatch('accessConflicts', form);
  const departmentConflicts = useWatch('departmentConflicts', form);
  const noNewConflictsAdded = accessConflicts
    ?.concat(departmentConflicts)
    ?.every((item) => targetUserExceptions.map((i) => i.accessRuleId)?.includes(item));

  const initialValues = {
    justification: '',
    accessConflicts:
      targetUserExceptions.filter((x) => !!x.accessRule.conflictAccessId).map((x) => x.accessRuleId) ?? [],
    departmentConflicts:
      targetUserExceptions.filter((x) => !!x.accessRule.departmentId).map((x) => x.accessRuleId) ?? [],
  };

  const columns: ColumnsType<AccessRuleWithDetail> =
    user.employeeType === EmployeeType.CONTINGENT_WORKER
      ? [
          {
            title: 'Employee Subtype',
            dataIndex: ['employeeSubtype'],
            filters: employeeSubtypeOptions,
            sorter: (a, b) => (a.employeeSubtype ?? '').localeCompare(b.employeeSubtype ?? ''),
          },
        ]
      : [];

  const handleOnFinish = (formData: FormData) => {
    // uncheck values that are options for rules
    const userExceptions =
      targetUserExceptions
        .map(({ accessRuleId }) => accessRuleId)
        .filter((id) => !loggedInUserRules.find((x) => x.id === id)) ?? [];

    // Get the diff between the original rules list and the updated form list. If no changes, do not call update API
    const changedConflicts = _.xor(
      [...formData.accessConflicts, ...formData.departmentConflicts],
      targetUserExceptions.map((i) => i.accessRuleId),
    );
    if (changedConflicts?.length === 0) {
      messageApi.warning('No changes detected');
      return;
    }

    // reselect anything selected
    const payload = {
      justification: formData.justification,
      rules: [...userExceptions, ...formData.accessConflicts, ...formData.departmentConflicts],
    };

    manage.mutate(payload, { onSuccess });
  };

  const handleOnReset = () => {
    form.resetFields();
    onReset();
  };

  return (
    <Form
      form={form}
      initialValues={initialValues}
      layout="vertical"
      style={{ width: '100%', minWidth: 1000 }}
      onFinish={handleOnFinish}
    >
      {contextHolder}
      <Form.Item label="Access Conflicts" name="accessConflicts">
        <SelectTable
          columns={[
            {
              title: 'Access',
              dataIndex: ['access', 'name'],
              filters: _.uniqBy(
                loggedInUserRules.filter((x) => !!x.conflictAccess),
                (x) => x.access.id,
              ).map((x) => ({ text: x.access.name, value: x.access.id })),
              filterSearch: true,
              onFilter: (value, record) => record.access.id === value,
              sorter: (a, b) => a.access.name.localeCompare(b.access.name),
            },
            {
              title: 'Conflict Access',
              dataIndex: ['conflictAccess', 'name'],
              filters: _.uniqBy(
                loggedInUserRules.filter((x) => !!x.conflictAccess),
                (x) => x.conflictAccess?.id,
              ).map((x) => ({ text: x.conflictAccess?.name, value: x.conflictAccess?.id ?? '' })),
              filterSearch: true,
              onFilter: (value, record) => record.conflictAccess?.id === value,
              sorter: (a, b) => (a.conflictAccess?.name ?? '').localeCompare(b.conflictAccess?.name ?? ''),
            },
            ...columns,
          ]}
          dataSource={loggedInUserRules.filter((x) => !!x.conflictAccess)}
          pagination={false}
        />
      </Form.Item>
      <Form.Item label="Department Conflicts" name="departmentConflicts">
        <SelectTable
          columns={[
            {
              title: 'Access',
              dataIndex: ['access', 'name'],
              filters: _.uniqBy(
                loggedInUserRules.filter((x) => !!x.department),
                (x) => x.access.id,
              ).map((x) => ({ text: x.access.name, value: x.access.id ?? '' })),
              filterSearch: true,
              onFilter: (value, record) => record.access.id === value,
              sorter: (a, b) => a.access.name.localeCompare(b.access.name),
            },
            {
              title: 'Department',
              dataIndex: ['department', 'name'],
              filters: _.uniqBy(
                loggedInUserRules.filter((x) => !!x.department),
                (x) => x.department?.id,
              ).map((x) => ({ text: x.department?.name, value: x.department?.id ?? '' })),
              filterSearch: true,
              onFilter: (value, record) => record.department?.id === value,
              sorter: (a, b) => (a.department?.name ?? '').localeCompare(b.department?.name ?? ''),
            },
            ...columns,
          ]}
          dataSource={loggedInUserRules.filter((x) => !!x.department)}
          pagination={false}
        />
      </Form.Item>
      <Form.Item
        label="Justification"
        name="justification"
        hidden={noNewConflictsAdded}
        rules={[{ required: !noNewConflictsAdded, message: 'Justification is required' }]}
        tooltip="Please include role and die"
      >
        <Input.TextArea allowClear />
      </Form.Item>
      <Space>
        <Button type="primary" htmlType="submit" disabled={manage.isPending}>
          Save
        </Button>
        <Button onClick={handleOnReset}>Reset</Button>
        <Button onClick={() => navigate(-1)}>Cancel</Button>
      </Space>
    </Form>
  );
};
